import { captureException, captureMessage } from "@sentry/react";
import parsePhoneNumber from "libphonenumber-js";
import { toast } from "sonner";

import { IPhoneNumber, PickOutboundPhoneNumberParams, SelectOutboundPhoneNumberParams } from "@/types/phone.types";

const getGeneralPriority = (phoneNumber: IPhoneNumber) => phoneNumber.priority ?? 0;

const matchesBrand = (phoneNumber: IPhoneNumber, selectedBrand: string) => {
    const normalizedBrand = selectedBrand.toLowerCase();
    return phoneNumber.brands?.some((brand) => brand.toLowerCase() === normalizedBrand) ?? false;
};

const sortByGeneralPriority = (phoneNumbers: IPhoneNumber[]) =>
    [...phoneNumbers].sort((left, right) => getGeneralPriority(right) - getGeneralPriority(left));

const getPhoneNumberCountry = (phoneNumber: IPhoneNumber) => {
    try {
        return parsePhoneNumber(phoneNumber.number)?.country;
    } catch {
        return undefined;
    }
};

const findByCountry = (phoneNumbers: IPhoneNumber[], calledNumber: string) => {
    try {
        const destinationCountry = parsePhoneNumber(calledNumber)?.country;
        if (!destinationCountry) {
            return phoneNumbers;
        }

        const matchingAssignedCountries = phoneNumbers.filter((phoneNumber) =>
            phoneNumber.countries?.includes(destinationCountry)
        );
        if (matchingAssignedCountries.length > 0) {
            return matchingAssignedCountries;
        }

        const matchingNumberCountry = phoneNumbers.filter(
            (phoneNumber) => getPhoneNumberCountry(phoneNumber) === destinationCountry
        );
        if (matchingNumberCountry.length > 0) {
            return matchingNumberCountry;
        }

        return phoneNumbers;
    } catch {
        return phoneNumbers;
    }
};

export const pickOutboundPhoneNumber = ({
    calledNumber,
    phoneNumbers,
    selectedBrand,
}: PickOutboundPhoneNumberParams): IPhoneNumber | undefined => {
    const accessibleNumbers = phoneNumbers.filter((phoneNumber) => phoneNumber.isActive);
    const brandNumbers = accessibleNumbers.filter((phoneNumber) => matchesBrand(phoneNumber, selectedBrand));
    if (brandNumbers.length === 0) {
        return undefined;
    }

    const countryNumbers = findByCountry(brandNumbers, calledNumber);
    const [selectedNumber] = sortByGeneralPriority(countryNumbers);
    return selectedNumber;
};

export const selectOutboundPhoneNumberAutomatically = ({
    calledNumber,
    phoneNumbers,
    selectedBrand,
    setIsBrandSelectorOpen,
    t,
}: SelectOutboundPhoneNumberParams) => {
    if (!phoneNumbers?.length) {
        toast.error(t("no_phone_numbers_found"));
        throw new Error("[Auto number] No phone numbers found");
    }

    if (!selectedBrand) {
        setIsBrandSelectorOpen(true);
        toast.error(t("no_brand_selected"));
        captureException(new Error("[Auto number] No brand selected"));
        return;
    }

    const selectedNumber = pickOutboundPhoneNumber({
        calledNumber,
        phoneNumbers,
        selectedBrand,
    });

    if (!selectedNumber) {
        toast.error(t("no_phone_numbers_found_for_brand"));
        captureMessage("[Auto number] No phone numbers found for brand");
        return;
    }

    return selectedNumber.number;
};
